// Typed wrappers for /api/v2/trades (list, reconcile, force-settle) and
// /api/v2/admin/transaction-status (reconcile queue).
// All endpoints are present in openapi.yaml and use apiClient.
import { apiClient } from "@/lib/api/client";
import type { components } from "@generated/api/schema";

export type AdminTrade = components["schemas"]["TradeResponse"];
export type AdminTradesPage = components["schemas"]["TradesPage"];
export type ReconcileResult = components["schemas"]["ReconcileResponse"];
export type ForceSettleBody = components["schemas"]["ForceSettleRequest"];
export type TransactionOverride =
  components["schemas"]["TransactionOverrideResponse"];
export type TransactionStatusList =
  components["schemas"]["TransactionStatusListResponse"];

export type TradeStatus = AdminTrade["status"];

export interface ListTradesParams {
  limit?: number;
  offset?: number;
}

export interface ListReconcileQueueParams {
  limit?: number;
  offset?: number;
}

export async function fetchAdminTrades(
  params: ListTradesParams,
): Promise<AdminTradesPage> {
  const { data, error } = await apiClient.GET("/api/v2/trades", {
    params: {
      query: { limit: params.limit ?? 50, offset: params.offset ?? 0 },
    },
  });
  if (error || !data)
    throw new Error(`fetchAdminTrades failed: ${JSON.stringify(error)}`);
  return data;
}

// #207: admin trade detail is admin-scoped (any owner) so support can audit
// another user's historical executionConfigSnapshot. The owner-scoped
// /api/v2/trade/{trade_id} 404s cross-user, hiding the snapshot from admins.
export async function fetchAdminTrade(tradeId: string): Promise<AdminTrade> {
  const { data, error } = await apiClient.GET(
    "/api/v2/admin/trades/{trade_id}",
    {
      params: { path: { trade_id: tradeId } },
    },
  );
  if (error || !data)
    throw new Error(
      `fetchAdminTrade failed id=${tradeId}: ${JSON.stringify(error)}`,
    );
  return data;
}

export async function reconcileTrade(
  tradeId: string,
): Promise<ReconcileResult> {
  const { data, error } = await apiClient.POST(
    "/api/v2/trades/{trade_id}/reconcile",
    {
      params: { path: { trade_id: tradeId } },
    },
  );
  if (error || !data)
    throw new Error(
      `reconcileTrade failed id=${tradeId}: ${JSON.stringify(error)}`,
    );
  return data;
}

export async function forceSettleTrade(
  tradeId: string,
  body: ForceSettleBody,
): Promise<AdminTrade> {
  const { data, error } = await apiClient.POST(
    "/api/v2/trades/{trade_id}/force-settle",
    {
      params: { path: { trade_id: tradeId } },
      body,
    },
  );
  if (error || !data)
    throw new Error(
      `forceSettleTrade failed id=${tradeId}: ${JSON.stringify(error)}`,
    );
  return data;
}

export async function fetchReconcileQueue(
  params: ListReconcileQueueParams,
): Promise<TransactionStatusList> {
  const { data, error } = await apiClient.GET(
    "/api/v2/admin/transaction-status",
    {
      params: {
        query: { limit: params.limit ?? 50, offset: params.offset ?? 0 },
      },
    },
  );
  if (error || !data)
    throw new Error(`fetchReconcileQueue failed: ${JSON.stringify(error)}`);
  return data;
}
